import { ArrowUpRight, Users, Home, Heart, School, Bot, Code2, Gamepad2, Lock, Cpu, Palette } from "lucide-react";

const GivingBack = () => {
    const impactStats = [
        { number: "14,800+", label: "Total Students Served", Icon: Users },
        { number: "93%", label: "Below Area Median Income", Icon: Home },
        { number: "49%", label: "Female & Non-Binary", Icon: Heart },
        { number: "53+", label: "Partner Schools & Programs", Icon: School },
    ];

    const programs = [
        { Icon: Bot, title: "AI & Machine Learning", description: "Teaching next-gen AI to underserved youth" },
        { Icon: Code2, title: "Web & App Development", description: "Future software engineers, from the ground up" },
        { Icon: Gamepad2, title: "Game Development", description: "Creative coding through interactive design" },
        { Icon: Lock, title: "Cybersecurity", description: "Preparing students for critical security roles" },
        { Icon: Cpu, title: "Robotics & IoT", description: "Hands-on hardware, sensors, and firmware" },
        { Icon: Palette, title: "CAD & Design", description: "3D modeling and digital design fundamentals" },
    ];

    const demographics = [
        { label: "Latino", percentage: 54 },
        { label: "Black/African American", percentage: 14 },
        { label: "Multi-Racial", percentage: 12 },
        { label: "White", percentage: 11 },
        { label: "Asian", percentage: 8 },
        { label: "Native", percentage: 1 },
    ];

    return (
        <section id="impact" className="relative py-32 border-t border-white/5 overflow-hidden">
            {/* Bg accent */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-accent-brand/[0.04] blur-[120px] rounded-full pointer-events-none"></div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
                {/* Header */}
                <div className="max-w-4xl mb-20">
                    <div className="text-[11px] font-mono uppercase tracking-widest text-accent-brand mb-4">
                        / Social Impact
                    </div>
                    <h2 className="text-5xl md:text-6xl lg:text-7xl font-display leading-[0.95] tracking-tight text-white mb-8">
                        Empowering communities
                        <br />
                        <em className="italic text-white/50">through technology.</em>
                    </h2>
                    <p className="text-white/60 text-lg md:text-xl leading-relaxed max-w-2xl">
                        Through our partnerships with{" "}
                        <span className="text-white">APS Training Academy</span> and{" "}
                        <span className="text-white">Ready Set STEM</span>, we&apos;re providing{" "}
                        <span className="text-accent-brand">FREE STEM education</span> to underserved communities — bridging the digital divide.
                    </p>
                </div>

                {/* Impact Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/10 mb-24">
                    {impactStats.map((s, i) => (
                        <div
                            key={i}
                            className="bg-ink-950 p-6 md:p-8"
                            data-testid={`impact-stat-${i}`}
                        >
                            <s.Icon className="w-5 h-5 text-accent-brand mb-4" strokeWidth={1.5} />
                            <div className="text-3xl md:text-4xl lg:text-5xl font-display text-white tracking-tight">
                                {s.number}
                            </div>
                            <div className="text-[11px] font-mono uppercase tracking-widest text-white/40 mt-2">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Two-column partner spotlight */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-24">
                    {/* APS Training Academy */}
                    <a
                        href="https://www.apstrainingacademy.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block rounded-2xl border border-white/10 bg-ink-950 p-8 md:p-10 hover:bg-ink-900 transition-all card-shine"
                        data-testid="partner-apsta"
                    >
                        <div className="flex items-start justify-between mb-6">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-brand/10 border border-accent-brand/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-accent-brand"></span>
                                <span className="text-[10px] font-mono uppercase tracking-widest text-accent-brand">
                                    501(c)(3) Non-Profit
                                </span>
                            </div>
                            <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-accent-brand transition-colors" />
                        </div>
                        <h3 className="text-3xl md:text-4xl font-display text-white tracking-tight mb-3">
                            APS Training Academy
                        </h3>
                        <p className="text-white/60 leading-relaxed mb-8">
                            Main sponsor of a non-profit providing FREE STEAM education to underserved communities in Aurora, IL and beyond.
                        </p>
                        <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/5">
                            <div>
                                <div className="text-2xl font-display text-white">2,800+</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-1">Students Served</div>
                            </div>
                            <div>
                                <div className="text-2xl font-display text-white">28</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-1">Programs</div>
                            </div>
                        </div>
                    </a>

                    {/* Ready Set STEM */}
                    <a
                        href="https://ready-set-stem.preview.emergentagent.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block rounded-2xl border border-white/10 bg-ink-950 p-8 md:p-10 hover:bg-ink-900 transition-all card-shine"
                        data-testid="partner-rss"
                    >
                        <div className="flex items-start justify-between mb-6">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-brand/10 border border-accent-brand/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-accent-brand"></span>
                                <span className="text-[10px] font-mono uppercase tracking-widest text-accent-brand">
                                    Best In STEM™ — Top 5%
                                </span>
                            </div>
                            <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-accent-brand transition-colors" />
                        </div>
                        <h3 className="text-3xl md:text-4xl font-display text-white tracking-tight mb-3">
                            Ready Set STEM
                        </h3>
                        <p className="text-white/60 leading-relaxed mb-8">
                            STEM.org-accredited coding, robotics, engineering, and science education for K-12 — with dual Best In STEM™ + Accredited™ trustmark.
                        </p>
                        <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/5">
                            <div>
                                <div className="text-2xl font-display text-white">12,000+</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-1">Learners</div>
                            </div>
                            <div>
                                <div className="text-2xl font-display text-white">50+</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-1">Courses</div>
                            </div>
                            <div>
                                <div className="text-2xl font-display text-white">98%</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mt-1">Satisfaction</div>
                            </div>
                        </div>
                    </a>
                </div>

                {/* Programs */}
                <div className="mb-24">
                    <div className="mb-10">
                        <div className="text-[11px] font-mono uppercase tracking-widest text-accent-brand mb-3">
                            / FREE Programs Offered
                        </div>
                        <h3 className="text-3xl md:text-4xl font-display text-white tracking-tight">
                            Six disciplines. Zero cost to qualifying families.
                        </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/10">
                        {programs.map((p, i) => (
                            <div
                                key={i}
                                className="bg-ink-950 hover:bg-ink-900 p-6 transition-colors"
                                data-testid={`program-card-${i}`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-accent-brand/10 border border-accent-brand/20 flex items-center justify-center">
                                        <p.Icon className="w-5 h-5 text-accent-brand" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-semibold mb-1">{p.title}</h4>
                                        <p className="text-white/50 text-sm">{p.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Demographics */}
                <div className="rounded-2xl border border-white/10 bg-ink-950 p-8 md:p-12">
                    <div className="mb-8">
                        <div className="text-[11px] font-mono uppercase tracking-widest text-accent-brand mb-3">
                            / Communities Served
                        </div>
                        <h3 className="text-3xl md:text-4xl font-display text-white tracking-tight">
                            Diverse by design.
                        </h3>
                    </div>
                    <div className="space-y-5 max-w-3xl">
                        {demographics.map((d, i) => (
                            <div key={i}>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-white/80">{d.label}</span>
                                    <span className="text-white font-mono font-semibold">{d.percentage}%</span>
                                </div>
                                <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="h-full bg-accent-brand transition-all duration-1000 ease-out"
                                        style={{ width: `${d.percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default GivingBack;
